/**
 * Voiceprint capture using AudioWorklet (the modern, non-deprecated Web Audio
 * API for sample-level capture). Reuses the same `audio-sender-processor.js`
 * worklet that the BYOD live recorder uses, so capture characteristics are
 * identical between voiceprint enrollment and live mic streaming.
 *
 * Usage:
 *
 *     const cap = createVoiceprintCapture();
 *     await cap.start();
 *     // ... user speaks ...
 *     const float32 = cap.stop();   // Float32Array @ 16kHz, mono
 *
 * The returned Float32Array is in [-1, 1]; the server converts it to int16
 * during upload (POST /api/v1/me/voiceprint with X-Audio-Encoding=pcm_f32le).
 */
export function createVoiceprintCapture(sampleRate = 16000) {
    let context = null;
    let source = null;
    let worklet = null;
    let stream = null;
    let chunks = [];

    async function start() {
        chunks = [];
        stream = await navigator.mediaDevices.getUserMedia({
            audio: { sampleRate, channelCount: 1 },
        });
        const Ctx = window.AudioContext || window.webkitAudioContext;
        context = new Ctx({ sampleRate });
        // /audio-sender-processor.js is shipped in /public and served at root
        // by nginx. Absolute path so it resolves correctly regardless of the
        // current page URL (e.g. /record-voiceprint, /student-app/..., /voiceprints).
        await context.audioWorklet.addModule('/audio-sender-processor.js');
        source = context.createMediaStreamSource(stream);
        worklet = new AudioWorkletNode(context, 'audio-sender-processor');
        worklet.port.onmessage = (event) => {
            // Each message is a Float32Array (4096-sample buffer from worklet).
            // Copy because the buffer is reused across messages.
            chunks.push(new Float32Array(event.data));
        };
        source.connect(worklet);
        worklet.connect(context.destination);
    }

    function stop() {
        try { worklet && worklet.disconnect(); } catch (_) {}
        try { source && source.disconnect(); } catch (_) {}
        try { context && context.close(); } catch (_) {}
        try { stream && stream.getTracks().forEach(t => t.stop()); } catch (_) {}

        const total = chunks.reduce((acc, c) => acc + c.length, 0);
        const merged = new Float32Array(total);
        let off = 0;
        for (const c of chunks) { merged.set(c, off); off += c.length; }
        // Drop references so GC can reclaim
        worklet = null; source = null; context = null; stream = null; chunks = [];
        return merged;
    }

    return { start, stop };
}
